import {
	faExclamationCircle,
	faInfoCircle,
	faWarning,
	type IconDefinition,
} from '@fortawesome/pro-solid-svg-icons';
import { type OrganizationLimitDto } from '@uturn/api/v3';
import {
	Callout,
	CalloutIcon,
	type CalloutProps,
	CalloutTitle,
	Icon,
} from '@uturn/ui-kit';
import { useTranslation } from 'react-i18next';
import { useOrganizationCreditLimit } from '../hooks/use-organization-credit-limit';
import {
	dismissBannerUntilEOD,
	isBannerDismissed,
} from '../modules/shipments/utils/banner-dismissal';
import { STORAGE_KEYS } from '../utils/storage-keys';

type InsuredLimitThresholdBannerProps = Omit<CalloutProps, 'children'>;

const determineVariant = (
	data: OrganizationLimitDto,
): CalloutProps['variant'] => {
	if (data.fullLimitReached) {
		return 'destructive';
	}
	if (data.warningLimitReached) {
		return 'warning';
	}

	return 'default';
};

const determineIcon = (data: CalloutProps['variant']): IconDefinition => {
	switch (data) {
		case 'destructive':
			return faExclamationCircle;
		case 'warning':
			return faWarning;
		default:
			return faInfoCircle;
	}
};

const useDetermineDescription = (
	data?: OrganizationLimitDto,
): string | undefined => {
	const { t } = useTranslation();

	if (!data) {
		return;
	}

	const creditUsagePercentage = Math.round(data.utilizationPercentage ?? 0);

	if (data.fullLimitReached) {
		return t(
			'pages.create-shipment.insured-limit-threshold-banner.description',
			'You have reached {{creditUsagePercentage}}% of your Credit Limit. Please monitor your outstanding balance to avoid publishing blocks.',
			{
				creditUsagePercentage,
			},
		).toString();
	}

	if (data.warningLimitReached) {
		return t(
			'pages.create-shipment.insured-limit-threshold-banner.description',
			'You have reached {{creditUsagePercentage}}% of your Credit Limit. Please monitor your outstanding balance to avoid publishing blocks.',
			{
				creditUsagePercentage,
			},
		).toString();
	}
};

const InsuredLimitThresholdBanner: React.FC<
	InsuredLimitThresholdBannerProps
> = ({ className, ...props }) => {
	const { limits, fullLimitReached, hasOrg } = useOrganizationCreditLimit();

	// determine if banner is dismissed now
	const isCreditUsageBannerDismissed = hasOrg
		? isBannerDismissed(STORAGE_KEYS.CREDIT_LIMIT)
		: false;

	// force show if fullLimitReached (destructive banner)
	const forceShow = fullLimitReached;

	const isCreditUsageBannerVisible =
		(!isCreditUsageBannerDismissed || forceShow) &&
		(limits?.warningLimitReached || limits?.fullLimitReached);

	const description = useDetermineDescription(limits);

	if (!isCreditUsageBannerVisible || !limits || !description) {
		return null;
	}

	const variant = determineVariant(limits);

	return (
		<Callout
			variant={variant}
			dismissible={!forceShow}
			onDismiss={
				!forceShow
					? () => dismissBannerUntilEOD(STORAGE_KEYS.CREDIT_LIMIT)
					: undefined
			}
			{...props}
		>
			<CalloutIcon>
				<Icon icon={determineIcon(variant)} />
			</CalloutIcon>
			<CalloutTitle>{description}</CalloutTitle>
		</Callout>
	);
};

export default InsuredLimitThresholdBanner;
